/**
 * Owner: Masa Bayu
 * File: create-production-catalog.ts
 * Date:Time: 2026-09-28 11:47
 * Description: RB-03 production catalog — title · description · tags.
 */

import {
  bindQxreadPublicationToQxp,
  createDraftCatalogPublication,
  createQxreadCatalogEntry,
  createQxreadPreviewBoundary,
  createQxreadPublicationMetadata,
  createQxreadVersionBinding,
  serializeCatalogEntry,
  transitionListingState,
  type QxreadCatalogSerialized,
} from '@qx/qxread-core';

function buildProductionEntry(input: {
  readonly publicationId: string;
  readonly title: string;
  readonly description: string;
  readonly qxpPublicationRef: string;
  readonly qxpVersionRef: string;
  readonly versionLabel: string;
  readonly authors: readonly string[];
  readonly tags: readonly string[];
  readonly language: string;
  readonly coverAssetRef: string;
}): QxreadCatalogSerialized {
  const draft = createDraftCatalogPublication({
    title: input.title,
    visibility: 'public',
  });
  const bound = bindQxreadPublicationToQxp(
    {
      ...draft.ref,
      publicationId: input.publicationId,
    },
    {
      qxpPublicationRef: input.qxpPublicationRef,
      listedVersion: input.versionLabel,
    },
  );
  const live = transitionListingState(
    {
      ...draft.lifecycle,
      publicationId: input.publicationId,
    },
    'live',
  );
  const metadata = createQxreadPublicationMetadata({
    publicationId: input.publicationId,
    title: input.title,
    description: input.description,
    authors: input.authors,
    tags: input.tags,
    language: input.language,
    coverAssetRef: input.coverAssetRef,
  });
  const version = createQxreadVersionBinding({
    publicationId: input.publicationId,
    qxpVersionRef: input.qxpVersionRef,
    versionLabel: input.versionLabel,
    integrityHash: `hash_${input.qxpVersionRef}`,
  });
  const preview = createQxreadPreviewBoundary({
    publicationId: input.publicationId,
    scope: 'sample',
  });
  return serializeCatalogEntry(
    createQxreadCatalogEntry({
      ref: bound,
      lifecycle: live,
      metadata,
      version,
      preview,
    }),
  );
}

/** Production host catalog — no qxrdpub_demo_* SoT · full cover set. */
const PRODUCTION_CATALOG: readonly QxreadCatalogSerialized[] = Object.freeze([
  buildProductionEntry({
    publicationId: 'qxrdpub_prod_creator',
    title: 'The Creator',
    description: 'A journey into divine mystery — maps, meaning, and the origin of creation.',
    qxpPublicationRef: 'qxp_pub_prod_creator',
    qxpVersionRef: 'qxp_ver_prod_creator',
    versionLabel: '1.0.0',
    authors: ['Masa Bayu'],
    tags: ['maps', 'science', 'malaysia'],
    language: 'en',
    coverAssetRef: 'asset_cover_prod_creator',
  }),
  buildProductionEntry({
    publicationId: 'qxrdpub_prod_relativity',
    title: 'Relativity Theory',
    description: 'Clear essays on space, time, and how relativity reshapes everyday intuition.',
    qxpPublicationRef: 'qxp_pub_prod_relativity',
    qxpVersionRef: 'qxp_ver_prod_relativity',
    versionLabel: '1.0.0',
    authors: ['Masa Bayu'],
    tags: ['essays', 'physics', 'united_states'],
    language: 'en',
    coverAssetRef: 'asset_cover_prod_relativity',
  }),
  buildProductionEntry({
    publicationId: 'qxrdpub_prod_why_islam',
    title: 'Why Islam',
    description: 'A reflective invitation to faith, practice, and the questions seekers ask.',
    qxpPublicationRef: 'qxp_pub_prod_why_islam',
    qxpVersionRef: 'qxp_ver_prod_why_islam',
    versionLabel: '1.0.0',
    authors: ['Masa Bayu'],
    tags: ['religion', 'essays', 'saudi_arabia'],
    language: 'ar',
    coverAssetRef: 'asset_cover_prod_why_islam',
  }),
  buildProductionEntry({
    publicationId: 'qxrdpub_prod_energy',
    title: 'Energy',
    description: 'How energy moves through systems — from physics to the living world around us.',
    qxpPublicationRef: 'qxp_pub_prod_energy',
    qxpVersionRef: 'qxp_ver_prod_energy',
    versionLabel: '1.0.0',
    authors: ['Masa Bayu'],
    tags: ['science', 'energy', 'singapore'],
    language: 'en',
    coverAssetRef: 'asset_cover_prod_energy',
  }),
  buildProductionEntry({
    publicationId: 'qxrdpub_prod_tenaga',
    title: 'Faktor Tenaga',
    description: 'Pengenalan tenaga dalam kehidupan harian — daripada sumber hingga kesan.',
    qxpPublicationRef: 'qxp_pub_prod_tenaga',
    qxpVersionRef: 'qxp_ver_prod_tenaga',
    versionLabel: '1.0.0',
    authors: ['Masa Bayu'],
    tags: ['maps', 'energy', 'malaysia'],
    language: 'ms',
    coverAssetRef: 'asset_cover_prod_tenaga',
  }),
  buildProductionEntry({
    publicationId: 'qxrdpub_prod_unsur',
    title: 'Faktor Unsur',
    description: 'Asas unsur dan bahan dalam sains — ringkas, jelas, dan relevan.',
    qxpPublicationRef: 'qxp_pub_prod_unsur',
    qxpVersionRef: 'qxp_ver_prod_unsur',
    versionLabel: '1.0.0',
    authors: ['Masa Bayu'],
    tags: ['science', 'elements', 'indonesia'],
    language: 'ms',
    coverAssetRef: 'asset_cover_prod_unsur',
  }),
  buildProductionEntry({
    publicationId: 'qxrdpub_book_khalifah12',
    title: 'Khalifah · Pages 1–12',
    description: 'Artifak QXP sebenar pertama — 12 halaman dipetakan daripada PDF (pipeline qxp-publish).',
    qxpPublicationRef: 'qxp_pub_book_khalifah12',
    qxpVersionRef: 'qxp_ver_book_khalifah12',
    versionLabel: '1.0.0',
    authors: ['Masa Bayu'],
    tags: ['history', 'ebook', 'malaysia'],
    language: 'ms',
    coverAssetRef: 'asset_cover_book_khalifah12',
  }),
  buildProductionEntry({
    publicationId: 'qxrdpub_book_writerdemo1',
    title: 'Sang Pelaut dan Bintang Utara',
    description: 'Artifak QXP pertama yang diemit terus oleh QXWriter (Fasa 2) — 3 bab fiksyen asal Bahasa Melayu.',
    qxpPublicationRef: 'qxp_pub_book_writerdemo1',
    qxpVersionRef: 'qxp_ver_book_writerdemo1',
    versionLabel: '1.0.0',
    authors: ['Masa Bayu'],
    tags: ['fiction', 'ebook', 'malaysia'],
    language: 'ms',
    coverAssetRef: 'asset_cover_book_writerdemo1',
  }),
]);

export function createProductionCatalog(): readonly QxreadCatalogSerialized[] {
  return PRODUCTION_CATALOG;
}

export function findProductionCatalogEntry(
  publicationId: string,
): QxreadCatalogSerialized | null {
  for (const entry of PRODUCTION_CATALOG) {
    if (entry.publicationId === publicationId) return entry;
  }
  return null;
}

export function isProductionCatalogPublicationId(publicationId: string): boolean {
  return publicationId.startsWith('qxrdpub_prod_');
}
